import type * as React from 'react';
import { cn } from '@wayste/shadcn/lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { Button } from './button';
import { Counter, type CounterVariant } from './counter';

/**
 * QuickFilter Component
 *
 * A toggleable pill-shaped chip for inline filtering. Designed for multi-select
 * scenarios where several filters can be active simultaneously.
 *
 * Selection is always signaled through border + text color — never a filled
 * background — so multiple selected chips don't overwhelm the page with color.
 *
 * Three tones cover the common cases:
 * - default     → standard filter (primary border + text when selected)
 * - destructive → filters scoped to error/failed states (always uses destructive
 *                 styling to pre-signal the severity of the data, even unselected)
 * - warning     → filters scoped to warning states
 *
 * @example
 * ```tsx
 * import { QuickFilter } from '@wayste/shadcn/components/quick-filter';
 *
 * // Uncontrolled — parent manages selected state
 * const [selected, setSelected] = useState(false);
 * <QuickFilter label="Active" count={54} selected={selected} onClick={() => setSelected(s => !s)} />
 *
 * // Destructive tone — counter and selection style signal error severity
 * <QuickFilter label="Has Errors" count={4} tone="destructive" selected={selected} onClick={...} />
 *
 * // Warning tone
 * <QuickFilter label="Needs Review" count={12} tone="warning" selected={selected} onClick={...} />
 *
 * // Without a count
 * <QuickFilter label="Flagged" selected={selected} onClick={...} />
 * ```
 */

// ─── Variants ─────────────────────────────────────────────────────────────────

const quickFilterVariants = cva(
    [
        'inline-flex items-center gap-1.5 rounded-full ring-1 ring-inset px-3 py-1 bg-surface-tertiary',
        'text-sm font-medium transition-colors select-none cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
    ].join(' '),
    {
        variants: {
            /**
             * Semantic tone — controls selection color and counter style.
             * - default     → primary color when selected
             * - destructive → destructive color always (pre-signals error data)
             * - warning     → warning color when selected
             */
            tone: {
                default: '',
                destructive: '',
                warning: '',
            },
            /**
             * Whether the chip is currently selected.
             * Unselected chips share a common neutral style regardless of tone.
             */
            selected: {
                false: 'ring-border-default text-text-secondary hover:ring-border-strong hover:text-text-primary',
                true: '',
            },
        },
        compoundVariants: [
            {
                selected: true,
                tone: 'default',
                className: 'ring-primary-border text-primary-on-surface-text',
            },
            {
                selected: true,
                tone: 'destructive',
                className: 'ring-destructive-border text-destructive-on-surface-text',
            },
            {
                selected: true,
                tone: 'warning',
                className: 'ring-warning-border text-warning-on-surface-text',
            },
        ],
        defaultVariants: {
            tone: 'default',
            selected: false,
        },
    },
);

// ─── Counter variant helper ───────────────────────────────────────────────────

// Destructive and warning chips always show their colored counter (even unselected)
// to pre-signal the severity of the data before the user clicks.
// Default chips use secondary when selected, default otherwise.
function resolveCounterVariant(tone: QuickFilterTone, selected: boolean): CounterVariant {
    if (tone === 'destructive') return 'alert';
    if (tone === 'warning') return 'warning';
    return selected ? 'secondary' : 'default';
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type QuickFilterTone = NonNullable<VariantProps<typeof quickFilterVariants>['tone']>;

export interface QuickFilterProps {
    /** The filter label displayed inside the chip. */
    label: string;
    /**
     * Optional count shown as a Counter pill inside the chip.
     * Omit when no numeric context is available.
     */
    count?: number;
    /**
     * Whether this chip is currently selected/active.
     * @default false
     */
    selected?: boolean;
    /**
     * Semantic tone — controls selection color and counter style.
     * - default     → primary styling when selected
     * - destructive → destructive styling always (pre-signals error data)
     * - warning     → warning styling when selected
     * @default 'default'
     */
    tone?: QuickFilterTone;
    /** Called when the chip is clicked. Parent is responsible for toggling selected state. */
    onClick?: () => void;
    /** Prevents interaction and dims the chip. */
    disabled?: boolean;
    className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const QuickFilter = ({ label, count, selected = false, tone = 'default', onClick, disabled = false, className }: QuickFilterProps) => {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={selected}
            className={cn(quickFilterVariants({ tone, selected }), className)}
        >
            {label}
            {count !== undefined && <Counter value={count} variant={resolveCounterVariant(tone, selected)} size="sm" />}
        </button>
    );
};
QuickFilter.displayName = 'QuickFilter';

// ─── QuickFilterGroup ─────────────────────────────────────────────────────────

export interface QuickFilterGroupProps {
    /** The `QuickFilter` chips belonging to this group. */
    children: React.ReactNode;
    /**
     * Called when "Clear all" is pressed. Omit to never render the button —
     * useful for single-select groups where clearing has no meaning.
     */
    onClearAll?: () => void;
    /**
     * Whether any chip in the group is currently selected. The clear button stays
     * in the layout either way and is only made visible when true.
     * @default false
     */
    hasActiveFilters?: boolean;
    /**
     * Text for the clear button.
     * @default 'Clear all'
     */
    clearLabel?: string;
    className?: string;
}

/**
 * Lays out a row of `QuickFilter` chips with a "Clear all" button immediately to
 * their right. Chips wrap onto more lines as needed.
 *
 * When `onClearAll` is supplied the button is always mounted and merely hidden
 * with `invisible` while nothing is selected. It keeps its space in the row, so
 * the chips never shift and the row never changes height when a filter becomes
 * active — the button is taller than a chip, so conditionally rendering it makes
 * the whole row jump. `visibility: hidden` also keeps it out of the tab order
 * and the accessibility tree while inactive.
 *
 * @example
 * ```tsx
 * <QuickFilterGroup hasActiveFilters={selected.size > 0} onClearAll={() => setSelected(new Set())}>
 *   {filters.map(({ id, label, count }) => (
 *     <QuickFilter key={id} label={label} count={count} selected={selected.has(id)} onClick={() => toggle(id)} />
 *   ))}
 * </QuickFilterGroup>
 * ```
 */
const QuickFilterGroup = ({
    children,
    onClearAll,
    hasActiveFilters = false,
    clearLabel = 'Clear all',
    className,
}: QuickFilterGroupProps) => {
    return (
        <div className={cn('flex items-center gap-3', className)}>
            <div className="flex flex-wrap items-center gap-2">{children}</div>
            {onClearAll && (
                <Button variant="ghost" size="sm" onClick={onClearAll} className={cn('shrink-0', !hasActiveFilters && 'invisible')}>
                    {clearLabel}
                </Button>
            )}
        </div>
    );
};
QuickFilterGroup.displayName = 'QuickFilterGroup';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { QuickFilter, QuickFilterGroup, quickFilterVariants };
